import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../../stories/atoms';
import { areaResultTranslations } from '../../../translations/areaResult';
import { StyledContainer, StyledIcon, StyledTypography } from './StoreVisibility.styles';

export type StoreVisibilityType = 'OPEN' | 'CLOSED' | 'REJECTED' | 'TEMPCLOSED' | 'FUTURE' | 'COUNTRYAPPROVED';

export const isStoreVisibilityType = (status: unknown): status is StoreVisibilityType =>
  ['OPEN', 'CLOSED', 'FUTURE', 'REJECTED', 'TEMPCLOSED', 'COUNTRYAPPROVED'].includes(
    status as StoreVisibilityType,
  );

export type StoreVisibilityProps = {
  type: StoreVisibilityType | '';
  closingDate?: string;
};

export const StoreVisibility: FC<StoreVisibilityProps> = ({ type, closingDate }) => {
  const { t } = useTranslation();
  const { storeVisibility: storeVisibilityTranslations } = areaResultTranslations;
  const VisibilityTypeMapping = {
    OPEN: {
      text: t(storeVisibilityTranslations.open.key, storeVisibilityTranslations.open.defaultValue),
      icon: Icons.CHECK_MARK_CIRCLE,
    },
    REJECTED: {
      text: t(storeVisibilityTranslations.rejected.key, storeVisibilityTranslations.rejected.defaultValue),
      icon: Icons.CLOSEROUND,
    },
    CLOSED: {
      text: t(storeVisibilityTranslations.closed.key, storeVisibilityTranslations.closed.defaultValue),
      icon: Icons.CLOSEROUND,
    },
    TEMPCLOSED: {
      text: t(storeVisibilityTranslations.tempClosed.key, storeVisibilityTranslations.tempClosed.defaultValue),
      icon: Icons.HOURLASS,
    },
    COUNTRYAPPROVED: {
      text: t(
        storeVisibilityTranslations.countryApproved.key,
        storeVisibilityTranslations.countryApproved.defaultValue,
      ),
      icon: Icons.CLOCK,
    },
    FUTURE: {
      text: t(storeVisibilityTranslations.future.key, storeVisibilityTranslations.future.defaultValue),
      icon: Icons.CLOCK,
    },
  };

  const isClosed = type === 'CLOSED' || type === 'REJECTED' || type === 'TEMPCLOSED';

  return (
    <StyledContainer
      width="fit-content"
      alignItems="center"
      status={type}
      padding={[1, 2]}
      borderRadius={[4]}
      gap={[1]}
    >
      <StyledIcon
        status={type}
        data-testid={type}
        icon={type === '' ? Icons.MINUSROUND : VisibilityTypeMapping[type].icon}
      />
      <StyledTypography status={type} type="legal">
        {type === ''
          ? t(storeVisibilityTranslations.noStatus.key, storeVisibilityTranslations.noStatus.defaultValue)
          : VisibilityTypeMapping[type].text}
      </StyledTypography>
      {isClosed && closingDate && closingDate !== '0000-00-00' && (
        <StyledTypography status={type} type="legal">{`- ${closingDate}`}</StyledTypography>
      )}
    </StyledContainer>
  );
};
